import {
  useQueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  UseMutateFunction,
} from '@tanstack/react-query';

import type { HelperResult, Entity } from '@openclassrooms/sdk-web';

import { getDependentEntities } from './dependentEntities';

type UpdateFunction<UpdateRequestParams, UpdateRequestBody, UpdateResponseBody> = (
  requestBody: UpdateRequestBody,
  params?: UpdateRequestParams
) => HelperResult<UpdateResponseBody>;

type MutationOptions<UpdateResponseBody, UpdateRequestBody> = Omit<
  UseMutationOptions<UpdateResponseBody, unknown, UpdateRequestBody>,
  'mutationFn'
> & { isOptimisticUpdateDisabled?: boolean };

type UseUpdateReturnType<EntityType, MutationRequestBodyType> = [
  UseMutateFunction<EntityType, unknown, MutationRequestBodyType>,
  Omit<UseMutationResult<EntityType, unknown, MutationRequestBodyType>, 'mutate'>
];

export default function getUseUpdate<
  UpdateRequestParams,
  EntityType extends Entity,
  UpdateRequestBody extends Partial<Entity> = Partial<EntityType>,
  UpdateResponseBody extends Partial<Entity> = Partial<EntityType>
>(
  update: UpdateFunction<UpdateRequestParams, UpdateRequestBody, UpdateResponseBody>,
  queryKey: string,
  queryKeyList: string,
  sharedCacheEntities: string[] = []
) {
  return (
    params?: UpdateRequestParams,
    mutationOptions: MutationOptions<UpdateResponseBody, UpdateRequestBody> = {}
  ): UseUpdateReturnType<UpdateResponseBody, UpdateRequestBody> => {
    const queryClient = useQueryClient();

    const { mutate, ...mutationResult } = useMutation({
      ...mutationOptions,
      mutationFn: async (mutatedData) => {
        const response = await update(mutatedData, params);
        return response?.body;
      },
      onMutate: async (mutatedData) => {
        const previousData = queryClient.getQueryData<EntityType>([queryKey, params]);

        if (previousData && !mutationOptions?.isOptimisticUpdateDisabled) {
          await queryClient.cancelQueries([queryKey, params]);

          queryClient.setQueryData(
            [queryKey, params],
            Array.isArray(previousData)
              ? previousData.concat(mutatedData)
              : {
                  ...previousData,
                  ...mutatedData,
                }
          );
        }

        if (mutationOptions?.onMutate) {
          mutationOptions.onMutate(mutatedData);
        }

        return { previousData };
      },
      onError: (error, mutatedData, context?: { previousData?: EntityType }) => {
        if (context?.previousData && !mutationOptions?.isOptimisticUpdateDisabled) {
          queryClient.setQueryData([queryKey, params], context.previousData);
        }

        if (mutationOptions?.onError) {
          mutationOptions.onError(error, mutatedData, context);
        }
      },
      onSettled: (data, error, variables, context) => {
        if (!mutationOptions?.isOptimisticUpdateDisabled) {
          queryClient.invalidateQueries({
            queryKey: [queryKey],
          });
          queryClient.invalidateQueries({
            queryKey: [queryKeyList],
          });

          new Set([...sharedCacheEntities, ...getDependentEntities(queryKey)]).forEach(
            (sharedCacheEntity) => {
              queryClient.invalidateQueries({
                queryKey: [sharedCacheEntity],
              });
            }
          );
        }

        if (mutationOptions?.onSettled) {
          mutationOptions.onSettled(data, error, variables, context);
        }
      },
    });

    return [mutate, mutationResult];
  };
}
